<script setup lang="ts">
import { useHead } from '@unhead/vue'
import { computed, onMounted, watch } from 'vue'
import AppShell from '@/components/layout/AppShell.vue'
import ToastHost from '@/components/ui/ToastHost.vue'
import WatchInviteHost from '@/components/watch/WatchInviteHost.vue'
import { useAppStore } from '@/stores/app'
import { useContentSourcesStore } from '@/stores/contentSources'
import { useLibraryStore } from '@/stores/library'
import { useSettingsStore } from '@/stores/settings'
import { useWatchTogetherStore } from '@/stores/watchTogether'

const appStore = useAppStore()
const contentSources = useContentSourcesStore()
const library = useLibraryStore()
const settings = useSettingsStore()
const watchTogether = useWatchTogetherStore()

useHead(computed(() => ({
    titleTemplate: title => title ? `${title} · CineFlow` : 'CineFlow',
    meta: [{ name: 'theme-color', content: settings.resolveTheme() === 'light' ? '#f5f7fb' : '#0b0b0c' }],
})))

async function syncAuthenticatedAccount(forceSources = false) {
    library.setIdentity(appStore.user?.id)
    if (!appStore.authenticated) {
        library.stopCloudSync()
        watchTogether.stopAll()
        contentSources.clear()
        return
    }
    watchTogether.startChannel()
    await contentSources.initialize(forceSources)
    if (appStore.authenticated && appStore.database) { await library.startCloudSync() }
}

watch(() => [appStore.initialized, appStore.authenticated, appStore.user?.id, appStore.isOwner] as const, ([initialized]) => {
    if (initialized) { void syncAuthenticatedAccount().catch(() => {}) }
}, { flush: 'post' })

async function refreshSessionData() {
    if (!appStore.initialized || document.visibilityState === 'hidden') { return }
    await appStore.initialize(true)
    await syncAuthenticatedAccount(true).catch(() => {})
    if (appStore.authenticated && appStore.database) { await library.refreshCloudSync().catch(() => {}) }
}

window.addEventListener('focus', () => void refreshSessionData())
document.addEventListener('visibilitychange', () => void refreshSessionData())

onMounted(async () => {
    settings.initializeTheme()
    await appStore.initialize()
    await syncAuthenticatedAccount().catch(() => {})
})
</script>

<template>
    <AppShell />
    <WatchInviteHost />
    <ToastHost />
</template>
